import { getFirstFewPosts, getHomePagesWithSlug } from "../lib/api";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../next-i18next.config";
import Head from "next/head";
import Layout from "../components/layout";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useState } from "react";
import HomeFeaturedTestimonials from "../components/home/home-featured-testimonials";
import Image from "next/image";

const Dialog = dynamic(() => import("../components/v2/dialog"), {
  ssr: false,
});

// Add loading skeletons to dynamic imports in index.tsx

const BlogHomeTab = dynamic(
  () => import("../components/v2/blog/blog-home-page"),
  {
    ssr: false,
    loading: () => <div style={{ minHeight: 400 }} aria-hidden className="blog-skeleton" />,
  }
);

const CallToAction = dynamic(
  () => import("../components/v2/call-to-action"),
  {
    ssr: false,
    loading: () => <div style={{ minHeight: 120 }} aria-hidden />,
  }
);

const HomeBenefitsCards = dynamic(
  () => import("../components/home/home-benefits-cards"),
  {
    ssr: false,  // change from ssr:true — it's below fold
    loading: () => <div style={{ minHeight: 300 }} aria-hidden />,
  }
);

const HomeOpsAiSection = dynamic(
  () => import("../components/home/home-ops-ai-section"),
  {
    ssr: false,
    loading: () => <div style={{ minHeight: 320 }} aria-hidden />,
  },
);

const ClientTabsTestimonials = dynamic(
  () => import("../components/v2/mw-carousel/testimonial-with-tabs"),
  {
    ssr: false,
    loading: () => (
      <div style={{ minHeight: 220 }} aria-hidden className="mw-review-tabs-skeleton" />
    ),
  },
);

const OurSolutions = dynamic(() => import("../components/v2/our-solutions"), {
  ssr: false,
  loading: () => <div style={{ minHeight: 400 }} aria-hidden />,
});
const MWWhyThis = dynamic(() => import("../components/v2/mw-why-this"), {
  ssr: false,
  loading: () => <div style={{ minHeight: 300 }} aria-hidden />,
});

const TrustedCompaniesV2 = dynamic(
  () => import("../components/v2/trusted-companies-v2"),
  {
    ssr: false,
    loading: () => <div style={{ minHeight: 80 }} aria-hidden />,
  },
);


export default function Index({ pageData, posts }) {
  const { t } = useTranslation("home");

  const homePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://middleware.io/#organization",
        name: "Middleware",
        url: "https://middleware.io/",
        logo: "https://middleware.io/images/middleware-logo.svg",
        description:
          "Middleware is a full-stack cloud observability platform that helps engineering teams monitor applications, infrastructure, logs, traces, and user experience from a single unified dashboard.",
        foundingDate: "2022",
        sameAs: [
          "https://www.linkedin.com/company/middleware-labs/",
          "https://x.com/middleware_labs/",
          "https://github.com/middleware-labs/",
        ],
      },
      {
        "@type": "SoftwareApplication",
        "@id": "https://middleware.io/#software",
        name: "Middleware Observability Platform",
        description:
          "Middleware is a cloud-native observability platform providing application performance monitoring, infrastructure monitoring, log management, distributed tracing, and real user monitoring in one unified system.",
        url: "https://middleware.io/",
        publisher: {
          "@id": "https://middleware.io/#organization",
        },
        applicationCategory: "DeveloperApplication",
        applicationSubCategory: "Observability Software",
        operatingSystem: "Web",
        softwareHelp: "https://docs.middleware.io/",
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.7",
          reviewCount: "320",
        },
        review: {
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: "5",
            bestRating: "5",
          },
          author: {
            "@type": "Person",
            name: "Verified Engineering User",
          },
          reviewBody:
            "Middleware provides excellent end-to-end visibility across applications, infrastructure, and logs with an intuitive UI and fast setup.",
        },
      },
    ],
  };

  const [isMwDemoImageOpen, setIsMwDemoImageOpen] = useState(false);
  const [testimonialVideoOpen, setTestimonialVideoOpen] = useState(false);
  const [testimonialVideoSrc, setTestimonialVideoSrc] = useState<string | null>(
    null
  );
  const [isNotVideo, setIsNotVideo] = useState(false);

  const handleTestimonialVideoOpen = (src: string, isNotVideo?: boolean) => {
    setTestimonialVideoSrc(src);
    setTestimonialVideoOpen(true);
    setIsNotVideo(isNotVideo);
  };

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          href="/images/Rethink-Observability-With-AI-mobile.webp"
          media="(max-width: 768px)"
          // @ts-ignore
          fetchpriority="high"
          imageSrcSet="/images/Rethink-Observability-With-AI-mobile.webp"
        />
        <link
          rel="preload"
          as="image"
          href="/images/Rethink-Observability-With-AI-1000w.webp"
          media="(min-width: 769px)"
          // @ts-ignore
          fetchpriority="high"
          imageSrcSet="/images/Rethink-Observability-With-AI-800w.webp 800w, /images/Rethink-Observability-With-AI-1000w.webp 1000w"
          imageSizes="(min-width: 769px) min(1000px, 56vw)"
        />
      </Head>

      <Layout
        isHomePage={true}
        pageTitle={pageData?.title}
        ogImage={pageData?.featuredImage?.node?.sourceUrl}
        uri={pageData?.uri}
        metaTitle={pageData?.seo?.title}
        metaDesc={pageData?.seo?.metaDesc}
        metaRobotsNoindex={pageData?.seo?.metaRobotsNoindex}
        metaRobotsNofollow={pageData?.seo?.metaRobotsNofollow}
      >
        <div className={"middle-wrapper site-page home-page"}>
          <section className={"hero-section"}>
            <div className={"container"}>
              <div className={"d-flex"}>
                <div className={"left-block"}>
                  <h1>
                    {t("home.hero.title")} <br />
                    <span className={"text-gradient"}>
                      {t("home.hero.withAi")}
                    </span>
                  </h1>

                  <p>{t("home.hero.subtitle")}</p>
                  <div className={"d-flex"}>
                    <Link
                      href={"https://app.middleware.io/auth/register/"}
                      target={"_blank"}
                      className={"sign-up ahref-btn primary"}
                    >
                      {t("cta.getStarted")}
                    </Link>
                    <Link
                      href={"https://docs.middleware.io"}
                      target={"_blank"}
                      className={"sign-up ahref-btn secondary"}
                    >
                      {t("cta.docs")}
                    </Link>
                  </div>
                </div>
                <div className={"right-block home-page"}>
                  <picture className="hero-lcp-picture">
                    <source
                      type="image/webp"
                      media="(max-width: 768px)"
                      srcSet="/images/Rethink-Observability-With-AI-mobile.webp"
                    />
                    <source
                      type="image/webp"
                      media="(min-width: 769px)"
                      sizes="(min-width: 769px) min(1000px, 56vw)"
                      srcSet="/images/Rethink-Observability-With-AI-800w.webp 800w, /images/Rethink-Observability-With-AI-1000w.webp 1000w, /images/Rethink-Observability-With-AI-1200w.webp 1200w, /images/Rethink-Observability-With-AI.webp 1337w"
                    />
                    <img
                      src="/images/Rethink-Observability-With-AI-mobile.webp"
                      alt="Middleware's Full-Stack Cloud Observability Platform"
                      width={1337}
                      height={904}
                      fetchPriority="high"
                      decoding="sync"
                      loading="eager"
                      className="hero-lcp-image"
                    />
                  </picture>
                </div>
              </div>
            </div>
          </section>

          <TrustedCompaniesV2
            className="home-page-customer"
            paragraph="Trusted By The Best Teams From"
          />

          <HomeOpsAiSection onOpenDemoVideo={() => setIsMwDemoImageOpen(true)} />

          <OurSolutions />

          <MWWhyThis />

          <section className="testimonial-section">
            <div className="container">
              <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
              <h2>Why <span>Organizations</span> Trust Middleware </h2>
              <HomeFeaturedTestimonials
                onVideoOpen={handleTestimonialVideoOpen}
              />

              <ClientTabsTestimonials />
            </div>
          </section>

          <HomeBenefitsCards />


          <BlogHomeTab postData={posts} />
          <CallToAction />
        </div>
      </Layout>

      <Dialog
        isOpen={testimonialVideoOpen}
        position="center"
        onClose={() => {
          setTestimonialVideoOpen(false);
          setTestimonialVideoSrc(null);
        }}
        size={isNotVideo ? "sx" : "md"}
        verticalCenter={true}
      >
        <div style={{ textAlign: "center" }}>
          {testimonialVideoSrc && !isNotVideo ? (
            <video
              key={testimonialVideoSrc}
              playsInline
              preload="metadata"
              autoPlay
              width="100%"
              height={450}
              controls={true}
            >
              <source src={testimonialVideoSrc} type="video/mp4" />
            </video>
          ) : isNotVideo ? <Image alt={"Testimonials Middleware"} height={520} width={360} src={testimonialVideoSrc} /> : null}
        </div>
      </Dialog>

      <Dialog
        isOpen={isMwDemoImageOpen}
        position="center"
        onClose={() => setIsMwDemoImageOpen(false)}
        size="md"
        verticalCenter={true}
      >
        <div style={{ textAlign: "center" }}>
          <video
            playsInline
            autoPlay
            loop
            preload="metadata"
            width="100%"
            height={450}
            controls={true}
          >
            <source src={"/images/middleware-demo.mp4"} type="video/mp4" />
          </video>
        </div>
      </Dialog>



    </>
  );
}

export async function getStaticProps(ctx: any) {
  const translations = await serverSideTranslations(
    ctx?.locale ?? "en",
    ["header", "footer", "home"],
    nextI18NextConfig,
  );

  const data = await getHomePagesWithSlug();
  const posts = await getFirstFewPosts();
  const pageData =
    data?.edges?.find((item) => item.node.isFrontPage === true)?.node || {};

  return {
    props: {
      ...translations,
      pageData: pageData,
      posts,
      pageTitle: pageData?.title,
      description: pageData?.seo?.metaDesc,
      ogImage:
        pageData?.featuredImage?.node?.sourceUrl ||
        "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png",
      uri: pageData?.uri,
      metaTitle: pageData?.seo?.title,
      metaDesc: pageData?.seo?.metaDesc,
      metaRobotsNoindex: pageData?.seo?.metaRobotsNoindex,
      metaRobotsNofollow: pageData?.seo?.metaRobotsNofollow,
    },
  };
}
